<script>
	import { appProfileState } from "../../state/ApplicationState.svelte";
	import {
		FormGroup,
		Input,
		Icon,
		Button,
	} from "@sveltestrap/sveltestrap";
	import * as Yup from "yup";
	import { useValidations } from "../../../../../SharedSvelte/Forms.svelte";
	import { baseUrl } from "../../../../../SharedSvelte/Common.svelte";
	import { Loader } from "../../../../../SharedSvelte/GlobalLoader.svelte";
	import Dialog from "../../../../../SharedSvelte/Dialog.svelte";

	const { validateField, validateForm, getErrors, resetErrors } = useValidations();
	
	const achSchema = Yup.object().shape({
		bankName: Yup.string().required("Institution Name is required"),
		routingNumber: Yup.string()
		 	.required("Routing Number is required")
		 	.test("is-valid-routingnumber", "Invalid Routing Number", isValidRoutingNumber),
		accountType: Yup.string()
			.required("ach Account Type is required")
			.oneOf(["Checking", "Savings"], "Invalid Account Type"),
		accountNumber: Yup.string().required("Account Number is required"),
		accountNumberConfirmation: Yup.string()
			.oneOf([Yup.ref("accountNumber"), null], "Account Numbers must match")
			.required("Confirmation Account Number is required"),
	});

	const otherSchema = Yup.object().shape({
		// routingNumber: Yup.string()
		// 	.required("Routing Number is required")
		// 	.test("is-valid-routingnumber", "Invalid Routing Number", isValidRoutingNumber),
		accountType: Yup.string().required("Account Type is required"),
		//.oneOf(["Checking", "Savings"], "Invalid Account Type"),
		// accountNumber: Yup.string().required("Account Number is required"),
		// accountNumberConfirmation: Yup.string()
		// 	.oneOf([Yup.ref("accountNumber"), null], "Account Numbers must match")
		// 	.required("Confirmation Account Number is required"),
	});

	let prompt;
	// @ts-nocheck
	// Svelte 5 runes are used for state management

	/**
	 * @typedef {'Premiums' | 'Claims' | 'Premiums & Claims Payment'} AccountUsageType - HRP usage types
	 * @typedef {'Savings' | 'Checking'} BankAccountType - Savings or Checking
	 * @typedef {'URS' | 'LTD' | 'HRA'} OtherAccountType - URS, LTD, HRA
	 * @typedef {BankAccountType | OtherAccountType} AllAccountTypes
	 * @typedef {object} FormData
	 * @property {AccountUsageType} usageType
	 * @property {AllAccountTypes} accountType
	 * @property {string} bankName
	 * @property {string} routingNumber
	 * @property {string} accountNumber
	 */

	// --- State Management with Runes ---
	//let { onsavecallback } = $props();

	/** @type {FormData} */
	let formData = $state({
		id: appProfileState.workingBankAccount?.id ?? null,
		usageType: appProfileState.workingBankAccount?.usageType ?? "Premiums", // Default to Premiums
		accountType: appProfileState.workingBankAccount?.accountType ?? "Checking", // Default to Checking
		bankName: appProfileState.workingBankAccount?.bankName ?? "",
		routingNumber: appProfileState.workingBankAccount?.routingNumber ?? "",
		accountNumber: appProfileState.workingBankAccount?.accountNumber ?? "",
		memberId: appProfileState.workingBankAccount?.memberId ?? "",
		accountNumberConfirmation: "",
	});

	//let isFormValid = $state(false);
	//let formHasErrors = $state(false);
	let saveError = $state("");
	let showRoutingNumberHelp = $state(false);

	// --- Derived State (Computed Runes) ---
	let errors = $derived(getErrors());
	let formHasErrors = $derived(Object.keys(errors).length ? true : false);

	//Default to ach schema. effect switches to other when needed.
	let formSchema = $state(achSchema);

	// Determine if the selected account type is a bank account (ACH)
	const isBankAccountSelected = $derived(
		formData.accountType === "Savings" || 
			formData.accountType === "Checking",
	);

	// Determine if URS/HRA/LTD specific fields should be shown
	const isOtherAccountSelected = $derived(
		formData.accountType === "URS" ||
			formData.accountType === "LTD" ||
			formData.accountType === "HRA",
	);

	// // Dynamic label for the "Account Name" field based on selection
	// const accountNameLabel = $derived.by(() => {
	//     switch (formData.accountType) {
	//         case 'URS': return 'URS Account Name';
	//         case 'LTD': return 'LTD Account Name';
	//         case 'HRA': return 'HRA Account Name';
	//         default: return 'Account Name (Optional)';
	//     }
	// });

	// --- Handlers & Logic ---
	function isValidRoutingNumber(number) {
		if (/^\d{9}$/.test(number)) {
			return number.split("")
				.map(function (num) { return parseInt(num); })
				.reduce(function (acc, num, i) {
					if (i === 0 || i === 3 || i === 6) {
						return acc + (3 * num);
					} else if (i === 1 || i === 4 || i === 7) {
						return acc + (7 * num);
					}
					return acc + num;
				}, 0) % 10 === 0;
		}
		return false;
	}

	function clearAllErrors() {
		resetErrors();
		saveError = "";
	}

	function cancelEditing() {
		appProfileState.showBankAccountEditor = false;
		appProfileState.workingBankAccount = null;
		window.scrollTo(0, 0);
	}

	function handleUsageChange(event) {
		const value = event.target.value;

		// If switching usage types, ensure a valid account type is still selected
		if (isOtherAccountSelected && !(value === "Premiums")) {
			formData.accountType = "Checking"; // Default to Checking for deposits (claims)
			formData.bankName = "";
		}
		formData.usageType = value;
		validateField(formSchema, "accountType", formData);
	}

	function handleAccountTypeChange(event) {
		const value = event.target.value;
		//formData.accountType = value;
		//console.log("handleAccountTypeChange", value, isOtherAccountSelected);
		// Auto-set Bank Name etc based on other account types
		if (value === "URS" || value === "LTD" || value === "HRA"){
			formData.bankName = value;
			formData.accountNumber = appProfileState.workingBankAccount?.memberId;
			formData.routingNumber = appProfileState.workingBankAccount?.memberId;
		} else if (isOtherAccountSelected) {
			//clear when changing from non ach type
			formData.bankName = "";
			formData.routingNumber = "";
			formData.accountNumber = "";
			formData.accountNumberConfirmation = "";
		}
		//else if (formData.accountType === 'URS' || formData.accountType === 'LTD' || formData.accountType === 'HRA') formData.bankName = ''; // Clear if coming from other to ACH
		clearAllErrors();
		formData.accountType = value;
	}

	function handleRoutingNumberChange(event) {
		validateField(formSchema, "routingNumber", formData);
	}

	function handleBankNameChange(event) {
		validateField(formSchema, "bankName", formData);
	}

	function handleAccountNumberChange(event) {
		validateField(formSchema, "accountNumber", formData);
	}

	function handleAccountNumberConfirmationChange(event) {
		validateField(formSchema, "accountNumberConfirmation", formData);
	}
	
	async function saveEdited() {
		//console.log("validating:", formData)
		await validateForm(formSchema, formData);
		if (formHasErrors) {
			//console.table(errors);
			return;
		} 

		//console.log("loader.display")
		Loader.display();
		//console.log("try")
		try {
			const response = await fetch(`${baseUrl}Options/Profile/SaveBankAccount`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					__RequestVerificationToken: document.getElementsByName("__RequestVerificationToken")[0].value,
				},
				body: JSON.stringify(formData),
			});
			if (!response.ok) {
				const error = `Server exception: ${response.status} ${response.statusText}`;
				//console.log(error);
				throw new Error();
			}

			const json = await response.json();
			if (!json.success) {
				//console.log(json.errors);
				throw new Error(json.errors);
			}
			
			//console.log(json.bankAccounts)
            appProfileState.bankAccounts = json.bankAccounts;
			//console.log("prompting")
			
			prompt.showMessage(
				"Your request has been received. Please allow 48 hours for your changes to be applied.", 
				"Success", 
				()=> {
					//console.log("resetting")
					appProfileState.showBankAccountEditor = false;
					appProfileState.workingBankAccount = null;
					clearAllErrors();
					window.scrollTo(0, 0);
				});
			
		} catch (err) {
			//console.log("error saving", err)
			saveError = "Failed to save account information. ";
			if (err.message) saveError += err.message;
		} finally {
			//console.log("loader.hide")
			Loader.hide();
		}
	}

	function handleSubmit(event) {
		event.preventDefault();
		saveEdited();
	}

	$effect(() => {
		formSchema = isBankAccountSelected ? achSchema : otherSchema;
	});
</script>

<main class="mb-5">
	<h1 class="mt-3 mb-3 ms-3">Account Information</h1>
	<form onsubmit={handleSubmit} novalidate>
		<div class="alert alert-info mb-3">
			<strong>How do you want to use this account?</strong>
			<div class="form-check">
				{#each ["Premiums", "Claims", "Premiums & Claims Payment"] as value}
					<div class="form-check">
						<input
							class="form-check-input"
							type="radio"
							name="usageOptions"
							id={`radio-${value.replace(/\s/g, "")}`}
							{value}
							bind:group={formData.usageType}
							onchange={handleUsageChange}
						/>
						<label
							class="form-check-label"
							for={`radio-${value.replace(/\s/g, "")}`}
						>
							{#if value === "Premiums"}
								I want to deduct from this account to pay my premium(s)
							{:else if value === "Claims"}
								I want to deposit into this account my claim reimbursements
							{:else}
								I want to use this account to both pay my premium(s) and deposit claim reimbursements
							{/if}
						</label>
					</div>
				{/each}
			</div>
		</div>
		
		<div class="row mb-3">
			<div class="col-xs-11 col-sm-11 col-md-8 col-lg-6">
				<FormGroup floating spacing="mb-3" label="Account Type">
					<Input
						type="select"
						value={formData.accountType}
						invalid={Boolean(errors.accountType)}
						feedback={errors.accountType}
						onchange={handleAccountTypeChange}
					>
						<option value=""></option>
						<optgroup label="Bank Accounts (ACH)">
							<option value="Checking">Checking</option>
							<option value="Savings">Savings</option>
						</optgroup>
						<!-- Only show Other Accounts option if usage type is Premiums -->
						{#if formData.usageType === "Premiums"}
							<optgroup label="Other Accounts (Deduction only)">
								<option value="URS">URS (Utah Retirement Systems)</option>
								<option value="LTD">LTD (Long Term Disability)</option>
								<option value="HRA">HRA (Health Reimbursement Arrangement)</option>
							</optgroup>
						{/if}
					</Input>
				</FormGroup>
			</div>
		</div>

		
		<!-- ACH Specific Fields (Routing/Account Number/Bank Name) -->
		{#if isBankAccountSelected}
			<div class="row mb-3">
				<div class="col-xs-11 col-sm-11 col-md-8 col-lg-6">
						<FormGroup
							floating
							spacing="mb-3"
							label="Institution Name"
						>
							<Input
								placeholder="e.g., Bank of America"
								type="text"
								bind:value={formData.bankName}
								invalid={Boolean(errors.bankName)}
								feedback={errors.bankName}
								onchange={handleBankNameChange}
							/>
						</FormGroup>
					</div>
			</div>
			
			<div class="row mb-3">
					<div class="col-xs-11 col-sm-11 col-md-8 col-lg-6">
						<FormGroup
							floating
							spacing="mb-3"
							label="Routing Number"
						>
							<Input
								placeholder="#########"
								type="text"
								bind:value={formData.routingNumber}
								invalid={Boolean(errors.routingNumber)}
								feedback={errors.routingNumber}
								onchange={handleRoutingNumberChange}
							/>
						</FormGroup>
					</div>
					<div class="col-xs-1 col-sm-1">
						<Button 
							color="link" 
							title="What's this"
							onclick={()=>{showRoutingNumberHelp = !showRoutingNumberHelp }}
						>
							<Icon name="question-circle" />
						</Button>
					</div>
				</div>

				{#if showRoutingNumberHelp}
					<div class="row mb-3">
						<div class="col-xs-12 col-sm-12 col-md-12 col-lg-12">
							<img
								src="{baseUrl}img/routing-number.png"
								alt="Routing Number"
								class="routing-image"
							/>
						</div>
					</div>
				{/if}


			<div class="row mb-3">
				<div class="col-xs-11 col-sm-11 col-md-8 col-lg-6">
						<FormGroup
							floating
							spacing="mb-3"
							label="Account Number"
						>
							<Input
								placeholder="############"
								type="text"
								bind:value={formData.accountNumber}
								invalid={Boolean(errors.accountNumber)}
								feedback={errors.accountNumber}
								onchange={handleAccountNumberChange}
							/>
						</FormGroup>
					</div>
			</div>

			<div class="row mb-3">
					<div class="col-xs-11 col-sm-11 col-md-8 col-lg-6">
						<FormGroup
							floating
							spacing="mb-3"
							label="Confirm Account Number"
						>
							<Input
								placeholder="#########"
								type="text"
								bind:value={formData.accountNumberConfirmation}
								invalid={Boolean(errors.accountNumberConfirmation)}
								feedback={errors.accountNumberConfirmation}
								onchange={handleAccountNumberConfirmationChange}
							/>
						</FormGroup>
					</div>
				</div>
		{/if}

		<div class="row">
			<div class="col">
				{#if saveError}
				    <p class="text-danger" role="alert">{saveError}</p>
			    {/if}
				<div class="button-group mt-3">
					<button
						type="button"
						class="btn btn-lg btn-secondary me-3"
						onclick={cancelEditing}>Cancel</button
					>
					<button
						type="submit"
						class="btn btn-lg btn-primary"
						disabled={formHasErrors}>Save</button
					>
				</div>
			</div>
		</div>
	</form>
</main>
<Dialog bind:this={prompt} />
